import { z } from "zod";
import { ActionOperationSchema } from "./actions";
import { AdvisorOperationSchema } from "./advisor";
import { BoardOperationSchema } from "./boards";
import { DistributionListOperationSchema } from "./distribution-lists";
import { GameMasterOperationSchema } from "./game-master";
import {
  InterventionOperationSchema,
  JumpOperationSchema,
  JumpRequestOperationSchema,
  WorldOperationSchema,
} from "./narrative";
import {
  AliasOperationSchema,
  PerspectiveChangeOperationSchema,
  PerspectiveHandleRefSchema,
  PerspectiveOperationSchema,
  PerspectiveSuggestionOperationSchema,
  SpectatorRefSchema,
} from "./perspectives";
import { IdSchema } from "./primitives";
import { SkillOperationSchema } from "./skills";
import { ThreadOperationSchema } from "./threads";
import { TimelineOperationSchema } from "./timeline";

/** Who authored a delta. The user behind it, when there is one, is the delta's `responsibleUser`. */
export const ActorSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("gameMaster") }),
  z.strictObject({ type: z.literal("perspective"), handle: IdSchema }),
  z.strictObject({ type: z.literal("user"), userId: IdSchema }),
  z.strictObject({ type: z.literal("system") }),
]);
export type Actor = z.infer<typeof ActorSchema>;

/** What a perspectives target names: a view, or a distribution list of perspectives. */
export const PerspectiveRefSchema = z.discriminatedUnion("type", [
  SpectatorRefSchema,
  PerspectiveHandleRefSchema,
  z.strictObject({ type: z.literal("distributionList"), id: IdSchema }),
]);
export type PerspectiveRef = z.infer<typeof PerspectiveRefSchema>;

export const DeltaTargetSchema = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("perspectives"),
    perspectives: z.tuple([PerspectiveRefSchema], PerspectiveRefSchema),
  }),
  z.strictObject({ type: z.literal("activeViews") }).meta({
    description:
      "The spectator and every perspective with at least one user when the delta is committed.",
  }),
  z.strictObject({ type: z.literal("users"), userIds: z.tuple([IdSchema], IdSchema) }),
  z.strictObject({ type: z.literal("gameMaster") }),
]);
export type DeltaTarget = z.infer<typeof DeltaTargetSchema>;

/**
 * How a delta is paced as it is watched. The world changes the moment it commits;
 * timing only holds back when viewers see it.
 */
export const DeltaTimingSchema = z.strictObject({
  delayMs: z
    .number()
    .int()
    .nonnegative()
    .max(60_000)
    .optional()
    .meta({
      description:
        "For a delta naming a scene, how far into that scene it starts, in the scene's own " +
        "seconds on screen. For any other, the least time between the previous delta " +
        "finishing and this one starting; time its author spent writing it counts toward that.",
    }),
  durationMs: z
    .number()
    .int()
    .positive()
    .max(60_000)
    .optional()
    .meta({
      description:
        "How long it plays: a message shows its author typing. A scene plays for its own " +
        "seconds instead; other changes have no duration.",
    }),
});
export type DeltaTiming = z.infer<typeof DeltaTimingSchema>;

export const OperationSchema = z.discriminatedUnion("type", [
  ...PerspectiveOperationSchema.options,
  ...PerspectiveSuggestionOperationSchema.options,
  ...PerspectiveChangeOperationSchema.options,
  ...AliasOperationSchema.options,
  ...DistributionListOperationSchema.options,
  ...TimelineOperationSchema.options,
  ...WorldOperationSchema.options,
  ...InterventionOperationSchema.options,
  ...JumpOperationSchema.options,
  ...JumpRequestOperationSchema.options,
  ...ThreadOperationSchema.options,
  ...ActionOperationSchema.options,
  ...BoardOperationSchema.options,
  ...AdvisorOperationSchema.options,
  ...SkillOperationSchema.options,
  ...GameMasterOperationSchema.options,
]);
export type Operation = z.infer<typeof OperationSchema>;
export type OperationType = Operation["type"];

/** A delta in the record. Its revision orders it, and is unique. */
export const CommittedDeltaSchema = z.strictObject({
  revision: z.number().int().positive(),
  actor: ActorSchema,
  responsibleUser: IdSchema.optional(),
  committedAt: z.iso.datetime({ offset: true }),
  target: DeltaTargetSchema,
  timing: DeltaTimingSchema.optional(),
  operation: OperationSchema,
});
export type CommittedDelta = z.infer<typeof CommittedDeltaSchema>;

/** A delta the game master or the system proposes: everything but what committing it adds. */
export const ProposedDeltaSchema = CommittedDeltaSchema.omit({ revision: true, committedAt: true });
export type ProposedDelta = z.infer<typeof ProposedDeltaSchema>;

/** A delta as a user receives it: only what reached them, without who sent it or to whom. */
export const UserDeltaSchema = CommittedDeltaSchema.pick({
  revision: true,
  committedAt: true,
  timing: true,
  operation: true,
});
export type UserDelta = z.infer<typeof UserDeltaSchema>;

/** A delta a user proposes. The game server fills in the actor, responsible user, and target. */
export const UserProposalSchema = UserDeltaSchema.omit({ revision: true, committedAt: true });
export type UserProposal = z.infer<typeof UserProposalSchema>;

export type WorldHistory = readonly CommittedDelta[];
export type UserHistory = readonly UserDelta[];
